// ===== forecastCells.ts — shared forecast-cell planning for the forecast layers (B1) =====
//
// Pure, DOM-free helpers shared by the weather-point, swell-point, pressure
// and wind-barb layers (C1/D1/E1/E2 build on top of this):
//
//   - ForecastPoint: THE one shared forecast-point shape — real forecast
//     cell coordinates, source time, selected forecast time (shared clock),
//     source name, and data type. Every forecast layer emits this shape.
//   - planForecastCells(): a map viewport → a bounded set of REAL forecast
//     cell centres, reusing windField's committed computeGridGeometry
//     (spacing selection, bbox snapping, point cap) — never a rebuild.
//   - cell dedupe: identical cell coordinates collapse across requests
//     (same 2-dp rounding the cache keys use).
//   - zoom-density: selectCellsForZoom() keeps fewer REAL cells at wide
//     zoom (stride decimation of the true grid) — never invents fake
//     screen points.
//   - cache keys: forecastCellsCacheKey() — data kind + cell bbox + forecast
//     run/window, so weather/swell/pressure never share a cache slot and a
//     new forecast window never reuses a stale grid.
//   - clock tie: forecastTimeFromClock() reads the shared forecast clock
//     (conditionsStore) and makeForecastPoint() stamps the selected hour
//     onto every point.
//
// The fetch layer itself lives in windField.ts (fetchForecastCells) so the
// committed 30-min cache / in-flight coalescing / epoch machinery is reused.

import type { ConditionsState } from '../stores/conditionsStore';
import {
  computeGridGeometry,
  DEG_TO_KM,
  LAT_CLAMP,
  nodeLat,
  nodeLon,
  type GridGeometry,
  type HourWindow,
  type WindGridBounds,
} from './windField';

// ── Shared forecast-point shape (B1-1) ──

export type ForecastDataType = 'weather' | 'swell' | 'pressure' | 'wind';

export type ForecastSource = 'open-meteo' | 'open-meteo-marine';

/** A real Open-Meteo forecast cell centre (lat/lon). */
export interface ForecastCell {
  lat: number;
  lon: number;
}

/**
 * THE shared forecast-point shape (B1-1). Used by weather, swell and
 * pressure data alike. Carries:
 *   - real forecast cell coordinates (lat/lon — never screen points)
 *   - sourceTime: ISO hour the model run started
 *   - forecastTime: ISO hour SELECTED on the shared forecast clock
 *   - source: source name ('open-meteo' | 'open-meteo-marine')
 *   - dataType: which kind of forecast this point carries
 */
export interface ForecastPoint {
  lat: number;
  lon: number;
  sourceTime: string;
  forecastTime: string;
  source: ForecastSource;
  dataType: ForecastDataType;
}

// ── Viewport → bounded real cell set (B1-2) ──

export interface ForecastCellPlan {
  /** The snapped grid geometry (spacing, bounds, cols/rows). */
  geometry: GridGeometry;
  /** Every real cell centre in the grid, row-major (j*cols + i). */
  cells: ForecastCell[];
}

/**
 * Turn a map viewport into a bounded set of REAL forecast cell centres.
 *
 * Reuses windField.computeGridGeometry — the committed spacing/bbox/cap
 * machinery — so cell selection is identical to the wind grid and pans
 * reuse snapped cells. The result is always a real Open-Meteo grid, never
 * an interpolated or screen-derived point set.
 */
export function planForecastCells(bounds: WindGridBounds, maxPoints?: number): ForecastCellPlan {
  const geometry = computeGridGeometry(bounds, maxPoints);
  const cells: ForecastCell[] = [];
  for (let j = 0; j < geometry.rows; j++) {
    const lat = nodeLat(geometry, j);
    for (let i = 0; i < geometry.cols; i++) {
      cells.push({ lat, lon: nodeLon(geometry, i) });
    }
  }
  return { geometry, cells };
}

// ── Fixed viewport sampler (v56: 25-cell Conditions budget) ──

/** Hard cap for the fixed viewport sample grid (weather + marine each). */
export const MAX_VIEWPORT_SAMPLE_POINTS = 25;

/** Globally aligned spacing candidates, finest first. */
export const VIEWPORT_SAMPLE_STEPS = [0.1, 0.25, 0.5, 1, 2, 5, 10] as const;

/**
 * Fixed, globally aligned viewport sampler for Conditions weather and swell.
 *
 * Replaces the up-to-600-cell field grid for area requests with a small grid
 * whose cells overlap across nearby viewports:
 *
 *   1. Adds the same 10% viewport padding the field planner uses.
 *   2. Clamps latitude to ±85 exactly like the field planner.
 *   3. Chooses the SMALLEST globally aligned spacing (VIEWPORT_SAMPLE_STEPS)
 *      whose snapped grid keeps rows * cols <= MAX_VIEWPORT_SAMPLE_POINTS.
 *   4. Snaps the bbox to global multiples of that spacing (floor west/south,
 *      ceil east/north) so pans that do not cross a sample-cell boundary
 *      reuse the identical cell set.
 *   5. Returns real cell centres in row-major order.
 *
 * Date-line and extreme-scale inputs behave exactly like the existing field
 * planner: bounds are padded and snapped without wrapping, and any input
 * whose snapped grid cannot fit the cap falls back to the coarsest step.
 */
export function planViewportSampleCells(bounds: WindGridBounds): ForecastCellPlan {
  const lonSpan = bounds.east - bounds.west;
  const latSpan = bounds.north - bounds.south;
  const padX = lonSpan * 0.1;
  const padY = latSpan * 0.1;

  const rawWest = bounds.west - padX;
  const rawEast = bounds.east + padX;
  const rawSouth = Math.max(-LAT_CLAMP, bounds.south - padY);
  const rawNorth = Math.min(LAT_CLAMP, bounds.north + padY);

  // Smallest globally aligned spacing whose snapped grid fits the cap.
  let spacing: number = VIEWPORT_SAMPLE_STEPS[VIEWPORT_SAMPLE_STEPS.length - 1];
  let west = 0;
  let east = 0;
  let south = 0;
  let north = 0;
  let cols = 0;
  let rows = 0;
  for (const s of VIEWPORT_SAMPLE_STEPS) {
    west = Math.floor(rawWest / s) * s;
    east = Math.ceil(rawEast / s) * s;
    south = Math.floor(rawSouth / s) * s;
    north = Math.ceil(rawNorth / s) * s;
    cols = Math.round((east - west) / s) + 1;
    rows = Math.round((north - south) / s) + 1;
    if (cols * rows <= MAX_VIEWPORT_SAMPLE_POINTS) {
      spacing = s;
      break;
    }
  }

  const snapped: WindGridBounds = {
    west: parseFloat(west.toFixed(2)),
    south: parseFloat(south.toFixed(2)),
    east: parseFloat(east.toFixed(2)),
    north: parseFloat(north.toFixed(2)),
  };
  const geometry: GridGeometry = {
    bounds: snapped,
    spacingDeg: spacing,
    spacingKm: spacing * DEG_TO_KM,
    cols,
    rows,
  };

  const cells: ForecastCell[] = [];
  for (let j = 0; j < geometry.rows; j++) {
    const lat = nodeLat(geometry, j);
    for (let i = 0; i < geometry.cols; i++) {
      cells.push({ lat, lon: nodeLon(geometry, i) });
    }
  }
  return { geometry, cells };
}

/**
 * Stable key for a sample-cell set: sorted 2-dp cell keys joined with '|'.
 * Identical cell sets always produce the same key regardless of order, so
 * viewport fetches and same-area fallbacks can be matched across pans.
 */
export function forecastCellSetKey(cells: ForecastCell[]): string {
  return cells
    .map((c) => cellKey(c.lat, c.lon))
    .sort()
    .join('|');
}

// ── Opt-in field budgets (v56 Task 11) ──
// The always-on Conditions summary stays at MAX_VIEWPORT_SAMPLE_POINTS (25).
// The opt-in layers get their own caps because their visuals need denser
// fields: Wind particles / Wind colour / Wind barbs share ONE wind field
// capped at 169 cells (13×13), and Pressure contours need 81 cells (9×9)
// to draw meaningful isobars.

/** Hard cap for the shared opt-in wind field (particles + colour + barbs). */
export const MAX_WIND_FIELD_CELLS = 169;

/** Fetch half a viewport beyond each screen edge for uninterrupted panning. */
export const WIND_FIELD_BUFFER_FRACTION = 0.5;

/** Hard cap for the opt-in pressure contour field. */
export const MAX_PRESSURE_FIELD_CELLS = 81;

/**
 * Plan the shared opt-in wind field for a viewport: the same snapped-grid
 * planner as the field layers, but coarsened to honor MAX_WIND_FIELD_CELLS.
 * Wind particles, Wind colour and Wind barbs all fetch through this one
 * geometry (and therefore one cache key + one request).
 */
export function planWindFieldCells(bounds: WindGridBounds): ForecastCellPlan {
  const lonSpan = Math.max(0, bounds.east - bounds.west);
  const latSpan = Math.max(0, bounds.north - bounds.south);
  const buffered = {
    west: bounds.west - lonSpan * WIND_FIELD_BUFFER_FRACTION,
    south: Math.max(-LAT_CLAMP, bounds.south - latSpan * WIND_FIELD_BUFFER_FRACTION),
    east: bounds.east + lonSpan * WIND_FIELD_BUFFER_FRACTION,
    north: Math.min(LAT_CLAMP, bounds.north + latSpan * WIND_FIELD_BUFFER_FRACTION),
  };
  const bufferedPlan = planForecastCells(buffered, MAX_WIND_FIELD_CELLS);
  if (bufferedPlan.cells.length <= MAX_WIND_FIELD_CELLS) return bufferedPlan;

  // At very wide regional views the 10° fallback grid cannot fit both the
  // viewport and the extra buffer under 169 points. Keep the request cap
  // authoritative and drop only the optional buffer for that view.
  return planForecastCells(bounds, MAX_WIND_FIELD_CELLS);
}

// ── Cell dedupe (B1-2: deduplicate identical cell coordinates) ──

/** Canonical 2-dp cell key — the same rounding the cache keys use. */
export function cellKey(lat: number, lon: number): string {
  return `${lat.toFixed(2)}:${lon.toFixed(2)}`;
}

// ── Request-mode integration (v56 Task 3) ──

/**
 * Sampling mode for a forecast-cell request:
 *   - 'viewport-summary': the fixed 25-cell Conditions budget
 *     (planViewportSampleCells) — identical and overlapping viewports
 *     reuse the same globally aligned cells;
 *   - 'field': the full snapped field grid (planForecastCells) — used by
 *     the pressure / weather / swell marker layers until their own
 *     budgets land.
 */
export type ForecastCellSampling = 'viewport-summary' | 'field';

/**
 * Pick the cell planner for a request's sampling mode. Conditions requests
 * use the 25-cell viewport summary; field renderers keep the full grid,
 * optionally capped (wind 169 / pressure 81 — v56 Task 11).
 */
export function planCellsForSampling(
  bounds: WindGridBounds,
  sampling: ForecastCellSampling,
  maxPoints?: number,
): ForecastCellPlan {
  return sampling === 'viewport-summary'
    ? planViewportSampleCells(bounds)
    : planForecastCells(bounds, maxPoints);
}

/**
 * Collapse identical cell coordinates (same 2-dp key) keeping first
 * occurrence order. Two overlapping viewports that resolve to the same
 * snapped grid produce one request, not two.
 */
export function dedupeCells(cells: ForecastCell[]): ForecastCell[] {
  const seen = new Set<string>();
  const out: ForecastCell[] = [];
  for (const c of cells) {
    const k = cellKey(c.lat, c.lon);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(c);
  }
  return out;
}

// ── Zoom-density helpers (B1-4) ──

/**
 * Stride for the real grid at a given zoom: every Nth row/column of the
 * TRUE forecast grid is kept. Wide zoom → fewer real cells; close zoom →
 * all of them. Never invents points — the output is always a subset of
 * the real grid the API actually serves.
 */
export function zoomCellStride(zoom: number): number {
  if (zoom >= 9) return 1; // close zoom — every real cell
  if (zoom >= 7) return 2; // every 2nd row/col
  if (zoom >= 5) return 4; // every 4th
  return 8; // regional zoom — every 8th real cell
}

/**
 * Pick the real cells to show at `zoom`: a stride-decimated subset of the
 * true grid (row-major `cells` from planForecastCells). Output is always
 * a strict subset of the input — no fake screen points.
 */
export function selectCellsForZoom(
  cells: ForecastCell[],
  cols: number,
  zoom: number,
): ForecastCell[] {
  const stride = zoomCellStride(zoom);
  if (stride <= 1) return [...cells];
  const out: ForecastCell[] = [];
  for (let i = 0; i < cells.length; i++) {
    const row = Math.floor(i / cols);
    const col = i % cols;
    if (row % stride === 0 && col % stride === 0) out.push(cells[i]);
  }
  return out;
}

// ── Cache keys (B1-3: kind + cell + forecast run/time) ──

/**
 * Cache key for a bounded cell-set request. Includes the data kind, the
 * snapped cell bbox (the "cells"), and the forecast run window — so:
 *   - weather/swell/pressure never share a slot
 *   - a different run/window never reuses a stale grid
 * Mirrors the committed wind grid key shape (`wind:grid:...`).
 */
export function forecastCellsCacheKey(
  kind: ForecastDataType,
  geometry: GridGeometry,
  hourWindow: HourWindow,
): string {
  const bbox = `${geometry.bounds.west.toFixed(2)},${geometry.bounds.south.toFixed(2)},${geometry.bounds.east.toFixed(2)},${geometry.bounds.north.toFixed(2)}`;
  const cellSelection = kind === 'weather' ? 'nearest' : 'sea';
  return `${kind}:cells:${geometry.spacingDeg}:${bbox}:${hourWindow.start}-${hourWindow.end}:${cellSelection}:best_match`;
}

// ── Shared forecast clock (B1-6) ──

/**
 * The selected forecast hour from the shared clock (conditionsStore).
 * Every forecast layer reads the SAME value, so weather/swell/pressure
 * markers all follow the clock together.
 */
export function forecastTimeFromClock(snapshot: ConditionsState): string {
  return snapshot.forecastTime.iso;
}

/** Build a shared ForecastPoint for a real cell at the given times. */
export function makeForecastPoint(
  cell: ForecastCell,
  sourceTime: string,
  forecastTime: string,
  source: ForecastSource,
  dataType: ForecastDataType,
): ForecastPoint {
  return { lat: cell.lat, lon: cell.lon, sourceTime, forecastTime, source, dataType };
}
